import { computed, defineComponent, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { ProjectGetter } from '@/store/project/project.module';
import { Project } from '@/store/project/project.model';
import { EnjoinAction, EnjoinGetter } from '@/store/enjoin/enjoin.module';
import { EnjoinSchema } from '@/store/enjoin/enjoin.validator';
import { Enjoin } from '@/store/enjoin/enjoin.model';
import BaseModalComponent from '@/components/baseModal/BaseModal.vue';

export default defineComponent({
  name: 'ProjectEnjoinFormComponent',
  components: {
    BaseModalComponent
  },
  props: {
    projectId: {
      type: Number,
    },
    enjoinId: {
      type: Number,
    },
  },
  setup: (props) => {
    const store = useStore();
    const isEditMode = computed(() => props.enjoinId != null);
    const title = computed(() => isEditMode.value ? 'แก้ไขการสั่งการหรือเร่งรัดโครงการ' : 'เพิ่มการสั่งการหรือเร่งรัดโครงการ');
    const project = computed<Project | undefined>(() => store.getters[ProjectGetter.BY_ID](props.projectId));
    const enjoin = computed<Enjoin | undefined>(() => store.getters[EnjoinGetter.BY_ID](props.enjoinId));
    const enjoinForm = ref<InstanceType<typeof BaseModalComponent>>();

    // form
    interface Form {
      projectId?: number | null;
      title?: string | null;
      description?: string | null;
    }
    const formData = reactive<Form>({
      projectId: props.projectId,
      title: '',
      description: '',
    });
    const validator = computed(() => EnjoinSchema.validate(formData, { abortEarly: false }));
    const errors = reactive({
      projectId: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'projectId')),
      title: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'title')),
      description: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'description')),
    });
    const isValid = computed(() => !errors.projectId && !errors.title && !errors.description);
    const isLoading = computed<boolean>(() => store.getters[EnjoinGetter.STATUS] === 'init' || store.getters[EnjoinGetter.STATUS] === 'loading');
    const submited = ref(false);

    // show modal
    const show = () => {
      new Promise<void>((resolve) => {
        if (isEditMode.value) {
          formData.title = enjoin.value?.title;
          formData.description = enjoin.value?.description;
        }
        resolve();
      })
      .then(() => enjoinForm.value?.show());
    };

    // hide modal
    const hide = () => {
      enjoinForm.value?.hide();
    };

    // clear submit status
    const unsubmit = () => {
      submited.value = false;
    };

    // clear form
    const clearForm = () => {
      new Promise<void>((resolve) => {
        formData.projectId = props.projectId;
        formData.title = '';
        formData.description = '';
        resolve();
      }).then(() => unsubmit());
    };

    // submit form
    const submitClick = () => {
      submited.value = true;

      if (isValid.value) {
        if (isEditMode.value) {
          store.dispatch(EnjoinAction.UPDATE, formData)
            .then(() => hide());
        } else {
          store.dispatch(EnjoinAction.CREATE, formData)
            .then(() => hide());
        }
      }
    };

    return {
      title,
      formData,
      isEditMode,
      errors,
      submited,
      isLoading,
      enjoinForm,
      project,
      show,
      hide,
      submitClick,
      unsubmit,
      clearForm,
    };
  },
});
